/**
 * Die Fortschrittszeile eines laufenden Uploads (Task 4, "Uploads bis 100 MB"): Dateiname, Phase
 * und - beim Senden - der Anteil in Prozent. Sie ist reine Anzeige; wer sie einhaengt (Task 5 bis
 * 7), versorgt `anteil` aus dem `fortschritt`-Rueckruf der Upload-Funktionen (`api/basis.ts#ladeHoch`).
 *
 * Zwei Phasen: `pruefen` (vor dem Senden, etwa die Analyse einer Datei im Browser; ohne Prozent,
 * der Balken laeuft unbestimmt) und `hochladen` (Senden; `anteil` 0 bis 1). Bei mehreren Dateien
 * einer Auswahl (`gesamt` > 1) zaehlt die Zeile mit: "Datei 2 von 3 · name · 50 %". Der Wortlaut
 * ist verbindlich (Task-4-Brief, Texttabelle). Prozent wird abgerundet: 100 % steht erst, wenn
 * `anteil` wirklich 1 ist. Das meldet der Browser schon, sobald der Koerper ganz gesendet ist; die
 * Antwort des Servers kann danach noch kurz ausstehen (gewollt, siehe Ruling im Ledger).
 *
 * Hilfstechnik: Der sichtbare Text (`.upload-fortschritt-text`) traegt den exakten Prozentwert, ist aber
 * KEINE Live-Region - bei 100 MB aendert er sich bis zu 100-mal je Datei, ein Screenreader sagte jede
 * Zahl an. Den genauen Stand gibt der Balken her: ein nativer `<progress>` (impliziter `progressbar`)
 * mit Namen "Upload ‹Dateiname›"; beim Hochladen mit `aria-valuenow`, in der Phase `pruefen` ohne
 * (unbestimmt).
 *
 * Angesagt wird ueber `UploadAnsage` (unten), NICHT ueber die Zeile: eine Live-Region, die mit ihrem ersten Inhalt
 * im selben Schritt entsteht, bleibt fuer Screenreader meist stumm, und die Zeile haengt der Halter erst beim
 * Uploadstart ein. Deshalb gilt hier die Hauskonvention von `App/StehendeHinweise.tsx` und `App/LadeHinweis.tsx`:
 *
 * - Die Region gehoert dem Halter und ist DAUERHAFT gemountet - ein `<UploadAnsage>` je Halter, unbedingt, auch
 *   wenn nichts laeuft. Ihr Inhalt kommt spaeter, als Text in eine schon vorhandene Region.
 * - `aria-atomic="false"`: die Rolle `status` bringt implizit `aria-atomic="true"` mit, ein Screenreader sagte bei
 *   jeder Aenderung die GANZE Region an. Laufen mehrere Uploads in einem Halter (Positionsdokumente: einer je
 *   Position), sagt er so nur das Neue an; jeder Lauf ist ein eigenes `<span>` unter seinem Stellen-Schluessel und
 *   bleibt dasselbe Element, solange der Lauf steht.
 * - Die Region steht INNERHALB des Containers ihres Halters. Ein `aria-modal`-Dialog (`SammelsendungDrawer`, der
 *   Ersatz-Dialog in `PositionsdokumentAktionen`) blendet fuer Hilfstechnik alles ausserhalb aus; eine zentrale
 *   Region in `App.tsx` wuerde dort nie angesagt. Darum gibt es keine, sondern eine je Halter.
 *
 * Der Text folgt dem Wortlaut der Zeile, aber nur in Stufen: 0, 25, 50, 75, 100 % - je Datei also hoechstens
 * "wird geprueft", 0, 25, 50, 75 und 100 %. Versteckt wird sie ueber die gemeinsame Klasse `visuell-versteckt`
 * (`styles/neubau/visuellVersteckt.css`), nie ueber `display: none`/`visibility: hidden`.
 */
export interface UploadFortschrittProps {
  dateiname: string;
  phase: "pruefen" | "hochladen";
  /** 0 bis 1; in der Phase `pruefen` ungenutzt. */
  anteil: number;
  /** Laufende Nummer in der Auswahl (ab 1), zusammen mit `gesamt`. */
  nummer?: number;
  gesamt?: number;
}

/** Schrittweite der Ansage in Prozent. */
const ANSAGE_STUFE = 25;

function fortschrittsText(
  dateiname: string,
  phase: UploadFortschrittProps["phase"],
  prozent: number,
  nummer: number | undefined,
  gesamt: number | undefined,
): string {
  if (phase === "pruefen") return `${dateiname} wird geprüft …`;
  if (nummer !== undefined && gesamt !== undefined && gesamt > 1) {
    return `Datei ${nummer} von ${gesamt} · ${dateiname} · ${prozent} %`;
  }
  return `${dateiname} wird hochgeladen … ${prozent} %`;
}

export function UploadFortschritt({ dateiname, phase, anteil, nummer, gesamt }: UploadFortschrittProps) {
  const prozent = Math.floor(anteil * 100);
  return (
    <div className="upload-fortschritt">
      <span className="upload-fortschritt-text">
        {fortschrittsText(dateiname, phase, prozent, nummer, gesamt)}
      </span>
      <progress
        className="upload-fortschritt-balken"
        aria-label={`Upload ${dateiname}`}
        aria-valuenow={phase === "hochladen" ? prozent : undefined}
        max={100}
        value={phase === "hochladen" ? prozent : undefined}
      />
    </div>
  );
}

/** Ein laufender Upload in der Ansage: `schluessel` ist die Upload-Stelle (`uploadStelle.*`), `anzeige` ihre Zeile. */
export interface UploadAnsageEintrag {
  schluessel: string;
  anzeige: UploadFortschrittProps;
}

/** Die Live-Region eines Halters (siehe Kopfkommentar): immer da, ein `<span>` je Eintrag, der Text in Stufen. */
export function UploadAnsage({ anzeigen }: { anzeigen: readonly UploadAnsageEintrag[] }) {
  return (
    <div className="upload-ansage visuell-versteckt" role="status" aria-atomic="false">
      {anzeigen.map(({ schluessel, anzeige: { dateiname, phase, anteil, nummer, gesamt } }) => {
        const stufe = Math.floor(Math.floor(anteil * 100) / ANSAGE_STUFE) * ANSAGE_STUFE;
        return <span key={schluessel}>{fortschrittsText(dateiname, phase, stufe, nummer, gesamt)}</span>;
      })}
    </div>
  );
}
